import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import {
  getHeaderMediaTypeMeta,
  renderBodyWithExamples,
} from "../../../utils/whatsappTemplateBuilder";

function formatPreviewTime() {
  const now = new Date();
  return now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function useObjectUrl(file) {
  const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url);
  }, [url]);
  return url;
}

function HeaderMediaPreview({ mediaType, file, previewUrl }) {
  const meta = getHeaderMediaTypeMeta(mediaType || "IMAGE");

  if (previewUrl && mediaType === "VIDEO") {
    return (
      <div className="wa-tpl-wa-bubble-media">
        <video
          src={previewUrl}
          className="wa-tpl-wa-bubble-media-el"
          muted
          playsInline
        />
        <div className="wa-tpl-wa-bubble-media-badge">
          <Icon icon="solar:play-circle-bold" width={28} />
        </div>
      </div>
    );
  }

  if (previewUrl && mediaType === "IMAGE") {
    return (
      <div className="wa-tpl-wa-bubble-media">
        <img src={previewUrl} alt="Header" className="wa-tpl-wa-bubble-media-el" />
      </div>
    );
  }

  if (file && mediaType === "DOCUMENT") {
    return (
      <div className="wa-tpl-wa-bubble-media wa-tpl-wa-bubble-media--document">
        <Icon icon="solar:document-linear" width={22} />
        <div>
          <strong>{file.name}</strong>
          <span>PDF document</span>
        </div>
      </div>
    );
  }

  return (
    <div className="wa-tpl-wa-bubble-media wa-tpl-wa-bubble-media--placeholder">
      <Icon icon={meta.icon} width={28} />
      <span>{meta.label} sample</span>
    </div>
  );
}

function CatalogPreview({ form, bodyText, footerText, thumbnailUrl }) {
  return (
    <div className="wa-tpl-wa-bubble">
      {thumbnailUrl ? (
        <div className="wa-tpl-wa-bubble-media">
          <img src={thumbnailUrl} alt="Catalog" className="wa-tpl-wa-bubble-media-el" />
        </div>
      ) : (
        <div className="wa-tpl-wa-bubble-media wa-tpl-wa-bubble-media--placeholder">
          <Icon icon="solar:gallery-linear" width={28} />
        </div>
      )}
      <div className={`wa-tpl-wa-bubble-body ${!bodyText ? "placeholder" : ""}`}>
        {bodyText || "Your message will appear here..."}
      </div>
      {footerText ? <div className="wa-tpl-wa-bubble-footer">{footerText}</div> : null}
      <div className="wa-tpl-wa-bubble-time">
        {formatPreviewTime()}
        <Icon icon="solar:check-read-bold" width={14} className="wa-tpl-read" />
      </div>
      <div className="wa-tpl-wa-bubble-buttons">
        <div className="wa-tpl-wa-bubble-btn">
          <Icon icon="solar:bag-4-linear" width={16} />
          <span>View Catalog</span>
        </div>
      </div>
    </div>
  );
}

function CarouselPreview({ form, bodyText }) {
  const cards = form.carouselCards || [];
  const [activeIndex, setActiveIndex] = useState(0);
  const safeIndex = Math.min(activeIndex, Math.max(0, cards.length - 1));

  return (
    <div className="wa-tpl-wa-bubble wa-tpl-wa-bubble--carousel">
      {bodyText ? <div className="wa-tpl-wa-bubble-body">{bodyText}</div> : null}
      <div className="wa-tpl-carousel-track-wrap">
        <div
          className="wa-tpl-carousel-track"
          style={{ transform: `translateX(-${safeIndex * 86}%)` }}
        >
          {cards.map((card) => (
            <CarouselCardPreview
              key={card.id}
              card={card}
              buttonType={form.carouselButtonType}
            />
          ))}
        </div>
      </div>
      {cards.length > 1 ? (
        <div className="wa-tpl-carousel-dots">
          {cards.map((card, idx) => (
            <button
              key={card.id}
              type="button"
              className={`wa-tpl-carousel-dot ${idx === safeIndex ? "active" : ""}`}
              onClick={() => setActiveIndex(idx)}
              aria-label={`Card ${idx + 1}`}
            />
          ))}
        </div>
      ) : null}
      <div className="wa-tpl-wa-bubble-time">
        {formatPreviewTime()}
        <Icon icon="solar:check-read-bold" width={14} className="wa-tpl-read" />
      </div>
    </div>
  );
}

function CarouselCardPreview({ card, buttonType }) {
  const imageUrl = useObjectUrl(card.imageFile);
  const btn = card.buttons?.find((b) => b.text?.trim());

  return (
    <div className="wa-tpl-carousel-card">
      <div className="wa-tpl-carousel-card-image">
        {imageUrl ? (
          <img src={imageUrl} alt="" />
        ) : (
          <Icon icon="solar:gallery-linear" width={24} />
        )}
      </div>
      <div className="wa-tpl-carousel-card-body">
        {card.bodyText?.trim() || "Card text..."}
      </div>
      {btn ? (
        <div className="wa-tpl-carousel-card-btn">
          {buttonType === "PHONE" ? (
            <Icon icon="solar:phone-linear" width={14} />
          ) : buttonType === "URL" ? (
            <Icon icon="solar:link-round-linear" width={14} />
          ) : null}
          <span>{btn.text}</span>
        </div>
      ) : (
        <div className="wa-tpl-carousel-card-btn muted">
          <Icon icon="solar:link-round-linear" width={14} />
          <span>Button</span>
        </div>
      )}
    </div>
  );
}

function StandardPreview({ form, showExamples }) {
  const headerFormat = form.headerFormat;
  const headerMediaType = form.headerMediaType || "IMAGE";
  const headerText = form.headerText?.trim();
  const bodyText = form.bodyText?.trim();
  const footerText = form.footerText?.trim();
  const examples = showExamples ? form.variableExamples : {};
  const mediaFile = headerFormat === "MEDIA" ? form.headerMediaFile : null;
  const mediaPreviewUrl = useObjectUrl(
    mediaFile && headerMediaType !== "DOCUMENT" ? mediaFile : null,
  );

  const previewBody = bodyText
    ? renderBodyWithExamples(bodyText, examples)
    : "Your message will appear here...";

  const buttons = [];
  if (form.buttonMode === "QUICK_REPLY") {
    form.quickReplyButtons
      .filter((b) => b.text?.trim())
      .forEach((b) => buttons.push({ type: "quick", label: b.text.trim() }));
  } else if (form.buttonMode === "CALL_TO_ACTION") {
    form.ctaButtons
      .filter((b) => b.text?.trim())
      .forEach((b) => {
        buttons.push({
          type: b.type === "PHONE" ? "phone" : "url",
          label: b.text.trim(),
        });
      });
  }

  return (
    <div className="wa-tpl-wa-bubble">
      {headerFormat === "MEDIA" ? (
        <HeaderMediaPreview
          mediaType={headerMediaType}
          file={mediaFile}
          previewUrl={mediaPreviewUrl}
        />
      ) : null}
      {headerFormat === "TEXT" && headerText ? (
        <div className="wa-tpl-wa-bubble-header">{headerText}</div>
      ) : null}
      <div className={`wa-tpl-wa-bubble-body ${!bodyText ? "placeholder" : ""}`}>
        {previewBody.split(/(\{\{\d+\}\})/g).map((part, idx) =>
          /^\{\{\d+\}\}$/.test(part) ? (
            <span key={idx} className="wa-tpl-var-chip">
              {part}
            </span>
          ) : (
            <span key={idx}>{part}</span>
          ),
        )}
      </div>
      {footerText ? <div className="wa-tpl-wa-bubble-footer">{footerText}</div> : null}
      <div className="wa-tpl-wa-bubble-time">
        {formatPreviewTime()}
        <Icon icon="solar:check-read-bold" width={14} className="wa-tpl-read" />
      </div>
      {buttons.length ? (
        <div className="wa-tpl-wa-bubble-buttons">
          {buttons.map((btn, idx) => (
            <div key={`${btn.label}-${idx}`} className="wa-tpl-wa-bubble-btn">
              {btn.type === "phone" ? (
                <Icon icon="solar:phone-linear" width={16} />
              ) : btn.type === "url" ? (
                <Icon icon="solar:link-round-linear" width={16} />
              ) : null}
              <span>{btn.label}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function TemplatePhonePreview({ form, showExamples = false }) {
  const bodyText = form.bodyText?.trim();
  const footerText = form.footerText?.trim();
  const catalogThumbUrl = useObjectUrl(form.catalogThumbnailFile);
  const templateType = form.templateType || "STANDARD";

  return (
    <div className="wa-tpl-preview-panel">
      <div className="wa-tpl-preview-panel-head">
        <Icon icon="solar:smartphone-2-linear" width={16} />
        <span>Live preview</span>
      </div>
      <div className="wa-tpl-phone-wrap">
        <div className="wa-tpl-phone">
          <div className="wa-tpl-phone-notch" />
          <div className="wa-tpl-phone-screen">
            <div className="wa-tpl-wa-header">
              <Icon icon="solar:arrow-left-linear" width={16} />
              <div className="wa-tpl-wa-header-avatar">B</div>
              <div className="wa-tpl-wa-header-meta">
                <strong>Business</strong>
                <span>online</span>
              </div>
              <div className="wa-tpl-wa-header-actions">
                <Icon icon="solar:videocamera-linear" width={14} />
                <Icon icon="solar:phone-linear" width={14} />
                <Icon icon="solar:menu-dots-linear" width={14} />
              </div>
            </div>

            <div className="wa-tpl-wa-chat">
              <div className="wa-tpl-wa-date-chip">Today</div>
              <div className="wa-tpl-wa-template-outgoing">
                {templateType === "CATALOG" ? (
                  <CatalogPreview
                    form={form}
                    bodyText={bodyText}
                    footerText={footerText}
                    thumbnailUrl={catalogThumbUrl}
                  />
                ) : templateType === "CAROUSEL" ? (
                  <CarouselPreview form={form} bodyText={bodyText} />
                ) : (
                  <StandardPreview form={form} showExamples={showExamples} />
                )}
              </div>
            </div>

            <div className="wa-tpl-wa-composer">
              <div className="wa-tpl-wa-composer-input">Type a message</div>
              <div className="wa-tpl-wa-send">
                <Icon icon="solar:plain-2-bold" width={14} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
